export type ProfileTextBlock =
  | { type: 'field'; label: string; value: string; placeholder?: boolean }
  | { type: 'paragraph'; text: string };

type ProfileTextParseResult =
  | { mode: 'structured'; blocks: ProfileTextBlock[] }
  | { mode: 'plain'; text: string };

export type ProfileContentLayout = 'default' | 'twoColumns' | 'crmGrid' | 'sectioned';

export type ProfileTextParseOptions = {
  /**
   * Treat `Заголовок:` lines with no value as section headers instead of plain paragraphs.
   */
  promoteEmptyColonLabels?: boolean;
};

export const CRM_PLACEHOLDER_TEXT = 'Здесь может быть информация из вашей CRM';

export const CRM_PROFILE_LABELS = [
  'Тип клиента',
  'Сумма заказов',
  'Количество заказов',
  'Средняя сумма заказа',
  'Категории интереса',
  'Объекты',
  'Основные товарные группы',
  'Характеристики товаров',
  'Дата последнего КП',
  'Текущий проект',
  'Рекламации',
  'Претензии',
  'Основные точки продаж',
  'Менеджеры проекта',
] as const;

const CRM_PROFILE_LABELS_NORMALIZED = new Set(
  CRM_PROFILE_LABELS.map((label) => label.trim().toLowerCase()),
);

export function isCrmProfileLabel(label: string): boolean {
  return CRM_PROFILE_LABELS_NORMALIZED.has(label.trim().toLowerCase());
}

export const AGE_TYPE_RANGES: Record<string, string> = {
  'Тип 1': 'до 25',
  'Тип 2': '25-35',
  'Тип 3': '35-50',
  'Тип 4': '50-65',
  'Тип 5': '65+',
};

const AGE_TYPE_PATTERN = /^тип\s*([1-5])[.,;]?$/i;
const LIST_MARKER_PATTERN = /^\s*(?:[-•*]|\d+[.)])\s+/;

export function stripLeadingListMarker(line: string): string {
  return line.replace(LIST_MARKER_PATTERN, '');
}

export function resolveAgeRange(value: string): string | null {
  const match = value.trim().match(AGE_TYPE_PATTERN);
  if (!match) return null;
  return AGE_TYPE_RANGES[`Тип ${match[1]}`] ?? null;
}

function normalizeNewlines(text: string): string {
  return text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\r\n/g, '\n');
}

export function parseProfileKeyValueText(
  text: string,
  options: ProfileTextParseOptions = {},
): ProfileTextParseResult {
  const { promoteEmptyColonLabels = false } = options;
  const normalized = normalizeNewlines(text);
  const trimmed = normalized.trim();
  if (!trimmed) {
    return { mode: 'structured', blocks: [] };
  }

  const blocks: ProfileTextBlock[] = [];
  for (const line of trimmed.split('\n')) {
    const lineTrimmed = stripLeadingListMarker(line.trim());
    if (!lineTrimmed) continue;

    const colonIndex = lineTrimmed.indexOf(':');
    if (colonIndex > 0) {
      const label = lineTrimmed.slice(0, colonIndex).trim();
      let value = lineTrimmed.slice(colonIndex + 1).trim();
      if (!value && !isStructuredProfileLabel(label)) {
        if (promoteEmptyColonLabels) {
          blocks.push({ type: 'field', label: `${label}:`, value: '' });
        } else {
          blocks.push({ type: 'paragraph', text: lineTrimmed });
        }
      } else if (isCrmProfileLabel(label)) {
        blocks.push({
          type: 'field',
          label,
          value: CRM_PLACEHOLDER_TEXT,
          placeholder: true,
        });
      } else {
        if (label.trim().toLowerCase() === 'возраст') {
          const ageRange = resolveAgeRange(value);
          if (ageRange !== null) {
            value = ageRange;
          }
        }
        blocks.push({
          type: 'field',
          label,
          value,
        });
      }
    } else {
      blocks.push({ type: 'paragraph', text: lineTrimmed });
    }
  }

  const hasFields = blocks.some((block) => block.type === 'field');
  if (!hasFields) {
    const plain = trimmed
      .split('\n')
      .map((line) => stripLeadingListMarker(line.trim()))
      .filter(Boolean)
      .join('\n');
    return { mode: 'plain', text: plain };
  }

  return { mode: 'structured', blocks };
}

const PROFILE_ITEM_ORDER = [
  'Короткая справка о клиенте',
  'Короткие рекомендации',
  'Длинная справка о клиенте',
  'Длинные рекомендации',
] as const;

export const SHORT_PROFILE_ITEM_NAMES = ['Короткая справка о клиенте', 'Короткий профиль'] as const;

export const SHORT_RECOMMENDATION_ITEM_NAMES = [
  'Короткая рекомендация',
  'Короткие рекомендации',
] as const;

export const LONG_PROFILE_ITEM_NAMES = ['Длинная справка о клиенте', 'Длинный профиль'] as const;

export const LONG_RECOMMENDATION_ITEM_NAMES = [
  'Длинная рекомендация',
  'Длинные рекомендации',
] as const;

/** Map profile section title → demo tour target id (or null if not a tour anchor). */
export function tourTargetForItemName(itemName: string): string | null {
  if (
    SHORT_RECOMMENDATION_ITEM_NAMES.includes(
      itemName as (typeof SHORT_RECOMMENDATION_ITEM_NAMES)[number],
    )
  ) {
    return 'short-recommendation';
  }
  if (SHORT_PROFILE_ITEM_NAMES.includes(itemName as (typeof SHORT_PROFILE_ITEM_NAMES)[number])) {
    return 'short-profile';
  }
  if (
    LONG_RECOMMENDATION_ITEM_NAMES.includes(
      itemName as (typeof LONG_RECOMMENDATION_ITEM_NAMES)[number],
    )
  ) {
    return 'long-recommendation';
  }
  if (LONG_PROFILE_ITEM_NAMES.includes(itemName as (typeof LONG_PROFILE_ITEM_NAMES)[number])) {
    return 'long-profile';
  }
  return null;
}

export const CRM_PRODUCT_GROUPS_LABEL = 'Основные товарные группы';
export const CRM_PRODUCT_GROUPS_TOUR_TARGET = 'crm-product-groups';

const SHORT_PROFILE_PRIMARY_LABELS = ['ФИО', 'Возраст', 'Тип клиента', 'Описание'] as const;

const LONG_PROFILE_IDENTITY_LABELS = ['ФИО', 'Возраст', 'Пол', 'Город/Регион', 'Описание'] as const;

const STRUCTURED_PROFILE_LABELS_NORMALIZED = new Set([
  ...CRM_PROFILE_LABELS.map((label) => label.toLowerCase()),
  ...LONG_PROFILE_IDENTITY_LABELS.map((label) => label.toLowerCase()),
  'психотип',
  'ценности',
  'статус',
  'какой категории товары предлагать',
  'какой категории материалы предлагать',
]);

function isStructuredProfileLabel(label: string): boolean {
  return STRUCTURED_PROFILE_LABELS_NORMALIZED.has(label.trim().toLowerCase());
}

export function isPrimaryProfileLabel(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return SHORT_PROFILE_PRIMARY_LABELS.some((primary) => primary.toLowerCase() === normalized);
}

export function splitBlocksForCrmGrid(blocks: ProfileTextBlock[]): {
  identity: ProfileTextBlock[];
  crm: ProfileTextBlock[];
} {
  const identity: ProfileTextBlock[] = [];
  const crm: ProfileTextBlock[] = [];
  for (const block of blocks) {
    if (block.type === 'field' && isCrmProfileLabel(block.label)) {
      crm.push(block);
    } else {
      identity.push(block);
    }
  }
  return { identity, crm };
}

export function profileContentLayoutForItem(itemName: string): ProfileContentLayout {
  if (SHORT_PROFILE_ITEM_NAMES.includes(itemName as (typeof SHORT_PROFILE_ITEM_NAMES)[number])) {
    return 'twoColumns';
  }
  if (LONG_PROFILE_ITEM_NAMES.includes(itemName as (typeof LONG_PROFILE_ITEM_NAMES)[number])) {
    return 'crmGrid';
  }
  if (
    LONG_RECOMMENDATION_ITEM_NAMES.includes(
      itemName as (typeof LONG_RECOMMENDATION_ITEM_NAMES)[number],
    )
  ) {
    return 'sectioned';
  }
  return 'default';
}

export function sortProfileItems<T extends { item: { name: string } }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const indexA = PROFILE_ITEM_ORDER.indexOf(a.item.name as (typeof PROFILE_ITEM_ORDER)[number]);
    const indexB = PROFILE_ITEM_ORDER.indexOf(b.item.name as (typeof PROFILE_ITEM_ORDER)[number]);
    const orderA = indexA === -1 ? PROFILE_ITEM_ORDER.length : indexA;
    const orderB = indexB === -1 ? PROFILE_ITEM_ORDER.length : indexB;
    return orderA - orderB;
  });
}
